import { resolveDocumentReuploadBlob } from './document-blob-store';
import { getFileUrl } from './file-upload-service';
import { hashQuoteEngineEvalFile } from './quote-engine-eval-file-hash';
import { requireCanonicalProjectUuid } from './project-media-write-boundary';
import { extractPdfText, extractEmbeddedImagesFromPdf, renderPdfToImages, detectPlanLikelihood } from '../utils/helpers';
import { analysePdfDocumentVisualEvidence, appendPdfDocumentVisualEvidence } from './pdf-document-visual-evidence';
import { callOpenRouterVision, getMaxOutputTokens } from './ai-api';
import { createConsecutiveFailureCounter, isTransientAiServiceKind } from './ai-service-failure';
import { pickModelForTask } from './model-selector';
import { PLAN_VISION_PROMPT } from '../utils/stage1-prompts-helpers';
import { createSelectedFileScope, fileMatchesSelectedScope } from './selected-file-scope';
import { classifyDocumentType, loadActiveBusinessIdentity } from './document-type-classifier';
import { buildStage1DeterministicClassificationMetadata } from './stage1-batch-classification';
import { documentCanonicalClassSource, isOperatorDocumentClassSource } from '../utils/document-classification';
import { assertEvidencePreparationCurrent, checkpointPreparedEvidence, type ProjectEvidencePreparationOptions, type ProjectEvidencePreparationResult } from './project-evidence-preparation-types';

type Row = Record<string, any>;
const goodVisualStatuses = new Set(['complete', 'not-required']);
const pages = (values: unknown[]) => [...new Set(values.map(Number).filter(n => Number.isInteger(n) && n > 0))].sort((a, b) => a - b);
const readable = (value: unknown): string => {
    const text = typeof value === 'string' ? value.trim() : '';
    return /^\[(?:PDF (?:contains|could not|extraction failed)|Could not extract|No extractable content)/i.test(text) ? '' : text;
};

// How long an accepted partial read of the SAME bytes is left alone. It depends
// on why it was partial (review, 2026-09-20). Re-reading is paid vision, and new
// text changes the document content that the Summary and Risk receipts
// fingerprint, so an unnecessary re-read repeats six to seven minutes of paid
// Stage 1 and 2 on a resumed job.
//   - 'ai-service': the pages failed because the AI service was briefly
//     unavailable. A retry soon after is likely to complete the document, and
//     re-running the stages that read it is then the right outcome.
//   - anything else (a photo-only PDF, pages the model could not label): the
//     same bytes will read the same way. Long enough that a run, its automatic
//     retries and a same-day 'Try again' never re-read it; a replaced file has
//     new bytes and is always read.
const PARTIAL_PDF_RETRY_AFTER_MS = { 'ai-service': 15 * 60 * 1000, content: 6 * 60 * 60 * 1000 };
// One fewer than the queue's three provider waits, on purpose: the call that
// follows an accepted partial can meet the same paused keys and needs a wait of
// its own (measured live on the video lane, 2026-09-20). The document still
// gets one retry after a full cooldown, and the project keeps one in reserve.
const TRANSIENT_SERVICE_FAILURES_BEFORE_ACCEPTING_PARTIAL = 2;
// In memory only: consecutive transient failures per project within this tab.
// A count that has gone quiet is forgotten (see createConsecutiveFailureCounter).
const TRANSIENT_SERVICE_FAILURES_QUIET_AFTER_MS = 20 * 60 * 1000;
const transientServiceFailuresByProject = createConsecutiveFailureCounter(TRANSIENT_SERVICE_FAILURES_QUIET_AFTER_MS);

/** Test seam: forget the per-project failure counts. */
export function _resetPdfTransientServiceFailuresForTests(): void {
    transientServiceFailuresByProject.reset();
}

function preparationError(code: string, name: string, reason: string): Error {
    return Object.assign(new Error(`PDF evidence is not ready for ${name}: ${reason}`), {
        code, category: 'intake-readiness', retryable: true,
        operatorAction: 'Keep the original PDF attached, restore its file/AI connection and retry preparation. Summary has not started.',
    });
}

function needsPreparation(doc: Row): boolean {
    if (doc.evidencePreparation?.kind === 'pdf') return true;
    const evidence = doc.pdfEvidence || doc.metadata?.pdfEvidence;
    const visual = doc.documentVisualEvidence;
    const content = readable(doc.content || doc.text || doc.extractedText || doc.ocrText);
    if (!content || doc.extractionFailed || doc.needsOcr || doc.needsOCR
        || doc.metadata?.needsOcr || doc.metadata?.needsOCR
        || doc.contentComplete === false || doc.contentTruncated === true
        || doc.metadata?.contentComplete === false || doc.metadata?.contentTruncated === true
        || evidence?.contentComplete === false || evidence?.contentTruncated === true) return true;
    if (visual && !goodVisualStatuses.has(visual.status)) return true;
    // Deterministic folder preprocessing only inspects the text layer. A simple
    // layout does not prove that embedded site photographs were read. Attended
    // imports already carry their governed visual receipt and are left alone.
    if (doc.metadata?.pdfEvidence && !goodVisualStatuses.has(visual?.status)) return true;
    if (doc.embeddedImageCoverage?.evidenceComplete === false) return true;
    const covered = new Set(evidence?.visionAnalysedPages || []);
    return (evidence?.pages || []).some((page: Row) => page?.layout?.needsVision && !covered.has(page.page));
}

async function sourceFile(doc: Row, options: ProjectEvidencePreparationOptions): Promise<File> {
    const name = String(doc.name || doc.originalName || 'document.pdf');
    let blob = await resolveDocumentReuploadBlob(doc, options.project.id);
    assertEvidencePreparationCurrent(options);
    const key = doc.pdfS3Key || doc.s3Key;
    if (!blob && typeof key === 'string' && key.split('/')[1] === 'projects' && key.split('/')[2] === options.project.id) {
        const url = await getFileUrl(key);
        assertEvidencePreparationCurrent(options);
        if (url) {
            const response = await fetch(url, { signal: options.signal });
            if (!response.ok) throw preparationError('PROJECT_PDF_SOURCE_UNAVAILABLE', name, `stored source returned HTTP ${response.status}`);
            blob = await response.blob();
            assertEvidencePreparationCurrent(options);
        }
    }
    if (!blob?.size) throw preparationError('PROJECT_PDF_SOURCE_UNAVAILABLE', name, 'the exact project-owned original bytes are unavailable');
    return new File([blob], name, { type: 'application/pdf', lastModified: Number(doc.lastModified) || 0 });
}

/**
 * Complete missing PDF evidence at the shared pre-Summary boundary. The same
 * text/rendering helpers and governed per-image vision contract used by Stage 1
 * own the extraction. Originals stay attached; transient renders never become
 * project photos or persisted data URLs. No project/storage writes live here.
 */
export async function prepareProjectPdfEvidence(options: ProjectEvidencePreparationOptions): Promise<ProjectEvidencePreparationResult> {
    assertEvidencePreparationCurrent(options);
    const projectId = String(options.project?.id || '');
    if (!projectId) throw preparationError('PROJECT_ID_REQUIRED', 'project', 'a saved Project identity is required');
    requireCanonicalProjectUuid(projectId, 'prepareProjectPdfEvidence');
    const selection = createSelectedFileScope(options.selectedFileIds);
    let documents = [...(Array.isArray(options.project.documents) ? options.project.documents : [])];
    let project = options.project;
    let changed = false;
    // The active business, awaited once and only when a PDF is read, so its
    // own past quote is not competitor_quote on a cold profile cache.
    let businessIdentity: Awaited<ReturnType<typeof loadActiveBusinessIdentity>> | null = null;
    for (let index = 0; index < documents.length; index++) {
        const doc = documents[index];
        const name = String(doc?.name || doc?.originalName || 'document.pdf');
        const isPdf = /\.pdf$/i.test(name) || doc?.ext === 'pdf' || doc?.contentType === 'application/pdf' || doc?.mimeType === 'application/pdf';
        if (!isPdf || !fileMatchesSelectedScope(doc, selection) || !needsPreparation(doc)) continue;
        assertEvidencePreparationCurrent(options);
        // A PDF already set aside as unreadable is not tried again on every call.
        if (options.setAsideUnreadableFiles && doc.evidencePreparation?.status === 'set-aside'
            && doc.evidencePreparation?.documentId === String(doc.id || '')) {
            options.onProgress?.(0, `PDF could not be read and was set aside: ${name}`);
            continue;
        }
        // Keep the document and its text as imported; record why it was not read
        // further, and carry on (Peter, 2026-10-22).
        const setAside = async (reason: string) => {
            documents[index] = {
                ...doc,
                evidencePreparation: {
                    schemaVersion: 1, kind: 'pdf', status: 'set-aside',
                    projectId, documentId: String(doc.id || ''),
                    setAsideAt: new Date().toISOString(), reason,
                },
            };
            project = { ...project, documents: [...documents] };
            changed = true;
            project = await checkpointPreparedEvidence(options, project);
            documents = [...project.documents];
            options.onProgress?.(100, `PDF could not be read and was set aside: ${name}`);
        };
        if (!businessIdentity) businessIdentity = await loadActiveBusinessIdentity();
        assertEvidencePreparationCurrent(options);
        let file: File;
        try {
            file = await sourceFile(doc, options);
        } catch (error) {
            if (!options.setAsideUnreadableFiles || (error as { code?: string })?.code !== 'PROJECT_PDF_SOURCE_UNAVAILABLE') throw error;
            await setAside((error as Error).message);
            continue;
        }
        const sourceSha256 = await hashQuoteEngineEvalFile(file, { signal: options.signal });
        assertEvidencePreparationCurrent(options);
        const prior = doc.evidencePreparation;
        if (prior?.schemaVersion === 1 && prior.kind === 'pdf' && prior.status === 'complete'
            && prior.projectId === projectId && prior.documentId === String(doc.id || '')
            && prior.sourceSha256 === sourceSha256
            && readable(doc.content || doc.text) && doc.contentComplete === true
            && doc.contentTruncated !== true && doc.extractionFailed !== true
            && goodVisualStatuses.has(doc.documentVisualEvidence?.status)) continue;
        // A partial read of this exact source was accepted a moment ago. Stage 1
        // prepares evidence twice (the runner, then Project Summary) and
        // needsPreparation() is true for every prepared PDF, so without this the
        // same pages are rendered and sent to vision again on every call. A
        // later deliberate retry, or a changed source, reads it again.
        const partialAtMs = Date.parse(String(prior?.partialAt || ''));
        if (prior?.schemaVersion === 1 && prior.kind === 'pdf' && prior.status === 'partial'
            && prior.projectId === projectId && prior.documentId === String(doc.id || '')
            && prior.sourceSha256 === sourceSha256
            && Number.isFinite(partialAtMs)
            && Date.now() - partialAtMs < PARTIAL_PDF_RETRY_AFTER_MS[prior.partialReason === 'ai-service' ? 'ai-service' : 'content']) {
            // Say so: a silent skip reads as 'the document was read'.
            options.onProgress?.(0, `PDF partly read a few minutes ago, not read again yet: ${name}`);
            continue;
        }

        options.onProgress?.(0, `Reading PDF evidence: ${name}`);
        const extraction: Row = await extractPdfText(file);
        assertEvidencePreparationCurrent(options);
        let content = extraction.success ? readable(extraction.text) : '';
        const embedded: Row = await extractEmbeddedImagesFromPdf(file, { quality: 0.85, withCoverage: true });
        assertEvidencePreparationCurrent(options);
        const traversal = embedded?.coverage;
        const totalPages = Number(traversal?.totalPages || extraction.pages || extraction.pageCount);
        const failures: Row[] = [];
        if (!Number.isInteger(totalPages) || totalPages < 1) {
            const unreadable = preparationError('PROJECT_PDF_EVIDENCE_INCOMPLETE', name, 'page coverage could not be established');
            if (!options.setAsideUnreadableFiles) throw unreadable;
            await setAside(unreadable.message);
            continue;
        }
        const pageEvidence = Array.isArray(extraction.pageEvidence) ? extraction.pageEvidence : [];
        // A drawing by its text and page sizes, never its file name (CN3).
        const pageSizes = (pageEvidence.length > 0 ? pageEvidence : (Array.isArray(extraction.pageGeometry) ? extraction.pageGeometry : []))
            .map((page: Row) => ({ width: page?.width, height: page?.height }));
        const isPlan = detectPlanLikelihood({ textSample: content, textLength: content.length, pages: pageSizes }).isPlan;
        const mustReadEveryPage = !content || extraction.needsOcr || extraction.contentComplete !== true || isPlan;
        const requiredPages = pages([
            ...(mustReadEveryPage ? Array.from({ length: totalPages }, (_, n) => n + 1) : []),
            ...pageEvidence.filter((page: Row) => page?.layout?.needsVision).map((page: Row) => page.page),
            ...(traversal?.pageRenderFallbackRequiredPages || []),
            ...(traversal?.failedPages || []), ...(traversal?.incompletePages || []),
        ]);
        if (!traversal || (traversal.status !== 'complete' && requiredPages.length === 0)) {
            failures.push({ stage: 'pdf-embedded-traversal', code: 'coverage_incomplete', reason: 'Embedded visual traversal did not prove complete coverage' });
        }
        const visuals: Row[] = (embedded?.images || []).map((image: Row) => ({
            source: 'pdf-embed', page: image.page, imageId: image.imageId,
            width: image.width, height: image.height, data: image.dataUrl,
        }));
        if (requiredPages.length) {
            const rendered: Row = await renderPdfToImages(file, { pageNumbers: requiredPages, scale: isPlan ? 2 : 1.6, quality: isPlan ? 0.9 : 0.85, withCoverage: true });
            assertEvidencePreparationCurrent(options);
            const images = Array.isArray(rendered?.images) ? rendered.images : [];
            const renderedPages = pages(images.map((image: Row) => image.page));
            const missing = requiredPages.filter(page => !renderedPages.includes(page));
            if (missing.length) failures.push({ stage: 'pdf-page-recovery', code: 'page_render_incomplete', reason: 'Required PDF pages could not be rendered', pages: missing });
            visuals.push(...images.map((image: Row) => ({
                source: 'pdf-page-fallback', page: image.page, imageId: `page-render-${image.page}`,
                width: image.width, height: image.height, data: image.data,
            })));
        }
        const visualResult = await analysePdfDocumentVisualEvidence({
            documentName: name, visuals, upstreamFailures: failures,
            analyseBatch: async ({ prompt, images }) => {
                assertEvidencePreparationCurrent(options);
                if (!options.apiKey) throw preparationError('AI_KEY_MISSING', name, 'the configured AI gateway is unavailable');
                const task = isPlan ? 'plan-analysis' : 'pdf-ocr';
                const model = pickModelForTask(task, options.model);
                const reading = await callOpenRouterVision({
                    apiKey: options.apiKey, model,
                    prompt: `${isPlan ? PLAN_VISION_PROMPT : 'Extract ALL readable text, including addresses, company/contact details, strata plans, quote references, work descriptions, tables and dates. Preserve the document structure and uncertainty.'}\n\n${prompt}`,
                    images: images.map(data => ({ data })), temperature: 0.1,
                    maxTokens: getMaxOutputTokens(model), task, pipelineId: task,
                    stageName: '1', projectId, signal: options.signal,
                    timeoutMs: undefined, responseFormat: undefined, reasoningEffort: undefined,
                    logicalCallId: undefined, supportOperation: undefined,
                });
                assertEvidencePreparationCurrent(options);
                return reading;
            },
        });
        // The governed lane records provider failures, so reassert cancellation
        // outside it too; STOP must not be converted into a partial success.
        assertEvidencePreparationCurrent(options);
        content = appendPdfDocumentVisualEvidence(content, visualResult.contentAppendix);
        const complete = (goodVisualStatuses.has(visualResult.receipt.status) || (!mustReadEveryPage && !!content)) && !!content;
        // Decided BEFORE the document is saved, because the saved receipt must
        // say whether this partial read was accepted. A read that is about to
        // be retried for a transient AI-service failure must not carry the
        // "accepted, skip for a while" stamp, or the retry would skip the very
        // document it exists to re-read.
        const serviceFailures: Row[] = (visualResult.receipt.failedVisuals || [])
            .filter((visual: Row) => !!visual?.aiServiceKind);
        // Credit, authentication, configuration, data policy or an expired
        // session. No document will fare better, and accepting the partial
        // here stamped it 'skip for 15 minutes': after the operator topped up
        // and pressed Try again, the quote was drafted without the RFQ.
        const operatorNeeded = complete ? undefined : serviceFailures
            .find((visual: Row) => visual.aiServiceTransient === false);
        const transientServiceKind = serviceFailures
            .filter((visual: Row) => isTransientAiServiceKind(visual.aiServiceKind))
            .map((visual: Row) => String(visual.aiServiceKind))[0];
        let retryForTransientService = false;
        if (complete) {
            // The service answered for this document. A text-layer PDF is
            // 'complete' even when every one of its vision calls failed, and
            // such a document is re-read on every pass; letting it clear the
            // count meant the next, scanned document never reached the limit.
            if (!transientServiceKind) transientServiceFailuresByProject.clear(projectId);
        } else if (transientServiceKind && !operatorNeeded) {
            const failures = transientServiceFailuresByProject.increment(projectId);
            retryForTransientService = failures < TRANSIENT_SERVICE_FAILURES_BEFORE_ACCEPTING_PARTIAL;
        }
        const classified = classifyDocumentType(name, content, businessIdentity || {});
        const classification = isOperatorDocumentClassSource(documentCanonicalClassSource(doc))
            ? {}
            : { ...buildStage1DeterministicClassificationMetadata(classified), type: classified.class };
        const pdfEvidence = {
            schemaVersion: 1, extractionMode: 'shared-stage1-pdf-evidence', totalPages,
            extractedPageCount: complete ? totalPages : pages(visualResult.receipt.analysedVisuals.map((item: Row) => item.page)).length,
            contentComplete: complete, contentTruncated: !complete, pages: pageEvidence,
            visionAnalysedPages: pages(visualResult.receipt.analysedVisuals.map((item: Row) => item.page)),
        };
        const processed = {
            ...doc, ...classification, content, text: content,
            extractionFailed: !content, contentComplete: complete, contentTruncated: !complete,
            needsOcr: !complete, needsOCR: !complete,
            pageCount: totalPages,
            pdfEvidence,
            ...(doc.metadata ? { metadata: { ...doc.metadata, needsOcr: !complete, needsOCR: !complete,
                contentComplete: complete, contentTruncated: !complete, pdfEvidence } } : {}),
            documentVisualEvidence: visualResult.receipt,
            evidencePreparation: {
                schemaVersion: 1, kind: 'pdf', projectId, documentId: String(doc.id || ''), sourceSha256,
                status: complete ? 'complete' : 'partial',
                ...(complete || retryForTransientService || operatorNeeded ? {} : {
                    partialAt: new Date().toISOString(),
                    partialReason: transientServiceKind ? 'ai-service' : 'content',
                }),
            },
        };
        documents[index] = processed;
        project = { ...project, documents: [...documents] };
        changed = true;
        assertEvidencePreparationCurrent(options);
        project = await checkpointPreparedEvidence(options, project);
        documents = [...project.documents];
        // Operator directive (2026-07-29, project-intake-readiness.ts): a
        // per-file gap must never strand the whole project. The partial read is
        // already saved above, marked incomplete, and the appendix tells the
        // model its visual coverage is partial. Attended Stage 1 degrades on
        // this same condition and carries on; only the queue stopped, which is
        // how one unlabelled page or one rate-limited vision batch ended a
        // project (recorded in production as PROJECT_PDF_EVIDENCE_INCOMPLETE).
        //
        // One case still surfaces: the pages failed because the AI SERVICE was
        // briefly unavailable (rate limit, paused key pool, provider outage).
        // That says nothing about the document, and accepting it would drop an
        // RFQ from the quote because of a one-minute wobble. Raise it as the
        // transient service failure it is, so the run waits the cooldown and
        // tries again. Bounded per project: once it has happened this many
        // times running, accept the partial read and carry on. A project whose
        // whole selection yields nothing usable is still stopped afterwards by
        // the readiness floor (evaluateProjectIntakeReadiness, 'content').
        if (operatorNeeded) {
            throw Object.assign(new Error(`${String(operatorNeeded.reason || 'The AI service refused the request')} (while reading ${name})`), {
                code: String(operatorNeeded.errorCode || 'PROJECT_PDF_AI_SERVICE_BLOCKED'),
                category: String(operatorNeeded.aiServiceKind), retryable: false,
            });
        }
        if (retryForTransientService) {
            throw Object.assign(new Error(`The AI service was temporarily unavailable while reading ${name}. QuoteMaker will wait and try again.`), {
                code: 'PROJECT_PDF_AI_SERVICE_UNAVAILABLE', category: transientServiceKind, retryable: true, retryAfterSeconds: 60,
            });
        }
        options.onProgress?.(100, complete ? `PDF evidence ready: ${name}` : `PDF evidence partly read: ${name}`);
    }
    return { project, changed };
}
